import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Wordmark } from "@/components/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPasswordPage() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 auth-noise" data-testid="forgot-password-page">
      <header className="p-8">
        <Link to="/login"><Wordmark /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-10 fade-up">
          {sent ? (
            <div data-testid="forgot-success">
              <MailCheck className="h-12 w-12 text-green-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Bağlantı gönderildi")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("Bu e-posta kayıtlıysa şifre sıfırlama bağlantısı gönderildi. E-posta kutunuzu (ve spam klasörünü) kontrol edin. Bağlantı 1 saat geçerlidir.")}
              </p>
              <Button asChild className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/login" data-testid="forgot-goto-login-button">{t("Giriş sayfasına dön")}</Link>
              </Button>
            </div>
          ) : (
            <div data-testid="forgot-form-wrap">
              <KeyRound className="h-12 w-12 text-blue-600" />
              <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0A1128] mt-6">
                {t("Şifremi unuttum")}
              </h1>
              <p className="mt-3 text-sm text-slate-600">
                {t("E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.")}
              </p>
              <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">{t("E-posta")}</Label>
                  <Input id="forgot-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@firma.com" data-testid="forgot-email-input"
                    className="focus-visible:ring-blue-600" />
                </div>
                <Button type="submit" disabled={sending} data-testid="forgot-submit-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Sıfırlama Bağlantısı Gönder")}
                </Button>
              </form>
              <p className="mt-6 text-sm text-slate-500">
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700" data-testid="forgot-back-login-link">
                  {t("Giriş sayfasına dön")}
                </Link>
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
